/**
 * Round 9 E9 -- which chains are derived for which kind of entity.
 *
 * A vendor page fired `/v1/twin/chains/resilience-state/vendor/<id>`
 * and `/v1/twin/chains/obligation/vendor/<id>` and took a 404 on each,
 * and the 404 was CORRECT: neither deriver has ever sealed a vendor.
 * `derive_all_resilience` seals ibs_service / service / capability and
 * treats a vendor as a propagation SOURCE only; the obligation deriver
 * narrows the catalogue for the same three. So the link was right and
 * the destination was not merely "not ready" -- it is never coming.
 *
 * That makes the repair a question of what the page should SAY, not of
 * what the backend should build. Asking produced an empty panel a
 * reader could not tell apart from one that had not been derived yet,
 * which is the same failure as a check reporting a clean verdict on a
 * question nobody asked.
 *
 * So: do not ask, and say which way the dependency actually runs. A
 * vendor has no resilience state of its own in this model; it has the
 * services that rest on it, and those carry the state.
 *
 * Kept in step with the backend by `test_derived_chain_kinds.py`, which
 * pins each deriver's sealed kinds and names this file. Widening a
 * deriver to a new kind fails there, loudly, rather than leaving a
 * surface quietly refusing to ask for something that now exists.
 */
import type { EntityKind } from "shared/api/types";

/** Kinds `derive_all_resilience` seals a snapshot for. */
export const RESILIENCE_KINDS: readonly EntityKind[] = [
  "ibs_service",
  "service",
  "capability",
];

/** Kinds `derive_all_obligations` seals a snapshot for. */
export const OBLIGATION_KINDS: readonly EntityKind[] = [
  "ibs_service",
  "service",
  "capability",
];

export function hasResilienceChain(kind: EntityKind): boolean {
  return RESILIENCE_KINDS.includes(kind);
}

export function hasObligationChain(kind: EntityKind): boolean {
  return OBLIGATION_KINDS.includes(kind);
}
